import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { COMPANY, PHONE_DISPLAY, SITE_URL } from "@/lib/api";
import { downloadHtmlPdf, legalDocHtml } from "@/lib/pdf";

const GDPR = {
  title: "Ochrana osobných údajov (GDPR)",
  slug: "ochrana-osobnych-udajov",
  intro: "Tieto zásady ochrany osobných údajov vysvetľujú, aké osobné údaje spracúvame, na aký účel, na akom právnom základe a aké práva máte ako dotknutá osoba v zmysle Nariadenia (EÚ) 2016/679 (GDPR) a zákona č. 18/2018 Z. z. o ochrane osobných údajov.",
  sections: [
    {
      h: "1. Prevádzkovateľ",
      p: [
        `Prevádzkovateľom osobných údajov je spoločnosť ${COMPANY.name}, so sídlom ${COMPANY.street}, ${COMPANY.city}, IČO: ${COMPANY.ico}, DIČ: ${COMPANY.dic}, IČ DPH: ${COMPANY.icdph}, zapísaná v Obchodnom registri ${COMPANY.court}, oddiel: ${COMPANY.section}, vložka č. ${COMPANY.insert} (ďalej len „prevádzkovateľ" alebo „my").`,
        `Kontakt: e-mail ${COMPANY.email}, telefón ${PHONE_DISPLAY}.`,
      ],
    },
    {
      h: "2. Aké údaje spracúvame",
      p: [
        "Spracúvame najmä údaje, ktoré nám sami poskytnete prostredníctvom kalkulačky, kontaktného alebo rezervačného formulára:",
      ],
      li: [
        "identifikačné a kontaktné údaje — meno, e-mailová adresa, telefónne číslo,",
        "údaje o nehnuteľnosti a dopyte — typ objektu, plocha fasády, obec/okres, požadovaný termín,",
        "údaje z hodnotení (recenzií), ak nám ich dobrovoľne poskytnete,",
        "technické údaje o návšteve — anonymizovaný identifikátor návštevníka, prezerané stránky, zdroj návštevy (za účelom merania a zlepšovania webu).",
      ],
    },
    {
      h: "3. Účel a právny základ spracúvania",
      li: [
        "vypracovanie cenovej ponuky a kontaktovanie na základe vášho dopytu — plnenie predzmluvných opatrení (čl. 6 ods. 1 písm. b GDPR),",
        "sprostredkovanie realizácie zateplenia overeným partnerom — plnenie zmluvy / oprávnený záujem (čl. 6 ods. 1 písm. b a f GDPR),",
        "zasielanie noviniek a marketingu — váš súhlas (čl. 6 ods. 1 písm. a GDPR), ktorý môžete kedykoľvek odvolať,",
        "meranie návštevnosti a zlepšovanie služieb — oprávnený záujem (čl. 6 ods. 1 písm. f GDPR),",
        "plnenie zákonných povinností (napr. účtovných a daňových) — čl. 6 ods. 1 písm. c GDPR.",
      ],
    },
    {
      h: "4. Príjemcovia údajov",
      p: [
        "Vaše údaje môžeme poskytnúť overeným realizačným partnerom v príslušnom regióne za účelom vypracovania ponuky a realizácie zákazky. Ďalej sprostredkovateľom, ktorí pre nás zabezpečujú technické služby (hosting, e-mailová komunikácia, analytika), a to na základe zmluvy o spracúvaní osobných údajov. Údaje neposkytujeme do tretích krajín bez primeraných záruk.",
      ],
    },
    {
      h: "5. Doba uchovávania",
      p: [
        "Osobné údaje uchovávame len po dobu nevyhnutnú na dosiahnutie účelu spracúvania — po dobu vybavenia dopytu a následne po dobu vyžadovanú príslušnými právnymi predpismi. Údaje spracúvané na základe súhlasu uchovávame do jeho odvolania.",
      ],
    },
    {
      h: "6. Vaše práva",
      p: ["Ako dotknutá osoba máte právo:"],
      li: [
        "na prístup k svojim osobným údajom,",
        "na opravu nesprávnych alebo neúplných údajov,",
        "na vymazanie (právo na zabudnutie),",
        "na obmedzenie spracúvania,",
        "na prenosnosť údajov,",
        "namietať proti spracúvaniu založenému na oprávnenom záujme,",
        "kedykoľvek odvolať udelený súhlas,",
        "podať sťažnosť na Úrad na ochranu osobných údajov SR (dataprotection.gov.sk).",
      ],
    },
    {
      h: "7. Cookies a analytika",
      p: [
        "Web používa nevyhnutné technické prostriedky na svoju prevádzku a anonymizované meranie návštevnosti za účelom zlepšovania obsahu. Nepoužívame ich na profilovanie jednotlivcov. Ukladanie údajov v prehliadači môžete kedykoľvek obmedziť v nastaveniach svojho prehliadača.",
      ],
    },
    {
      h: "8. Kontakt vo veci ochrany údajov",
      p: [
        `V prípade otázok alebo uplatnenia svojich práv nás kontaktujte na e-maile ${COMPANY.email} alebo telefonicky na ${PHONE_DISPLAY}.`,
      ],
    },
  ],
};

const TERMS = {
  title: "Obchodné podmienky",
  slug: "obchodne-podmienky",
  intro: "Tieto obchodné podmienky upravujú podmienky používania webovej stránky Zateplenia.eu, spracovanie nezáväzných dopytov a sprostredkovanie realizácie zateplenia overenými partnermi.",
  sections: [
    {
      h: "1. Prevádzkovateľ",
      p: [
        `Prevádzkovateľom webovej stránky Zateplenia.eu je ${COMPANY.name}, ${COMPANY.street}, ${COMPANY.city}, IČO: ${COMPANY.ico}, DIČ: ${COMPANY.dic}, IČ DPH: ${COMPANY.icdph}, zapísaná v Obchodnom registri ${COMPANY.court}, oddiel: ${COMPANY.section}, vložka č. ${COMPANY.insert}.`,
      ],
    },
    {
      h: "2. Charakter služby",
      p: [
        "Zateplenia.eu poskytuje online kalkulačku orientačnej ceny zateplenia a sprostredkúva kontakt medzi záujemcom a overeným realizačným partnerom. Ceny zobrazené v kalkulačke sú orientačné a nezáväzné; presná cenová ponuka je vypracovaná na základe obhliadky a konkrétnych parametrov objektu.",
      ],
    },
    {
      h: "3. Nezáväzný dopyt",
      p: [
        "Odoslaním formulára vzniká nezáväzný dopyt. Odoslanie dopytu nie je objednávkou ani uzavretím zmluvy. Na základe dopytu vás kontaktujeme, prípadne vás spojíme s partnerom, ktorý vám vypracuje ponuku. Prípadná zmluva o dielo je uzatváraná priamo medzi klientom a realizačným partnerom.",
      ],
    },
    {
      h: "4. Cenové ponuky a záruka",
      p: [
        "Orientačné ceny vychádzajú z priemerných trhových cien materiálu, práce, lešenia a dopravy a môžu sa líšiť od finálnej ponuky. Na realizačné práce poskytujeme záruku 36 mesiacov a na dodaný materiál 60 mesiacov, ak nie je v konkrétnej zmluve dohodnuté inak.",
      ],
    },
    {
      h: "5. Práva a povinnosti",
      p: [
        "Používateľ sa zaväzuje uvádzať pravdivé údaje. Prevádzkovateľ nezodpovedá za škody vzniknuté nesprávne zadanými údajmi. Prevádzkovateľ si vyhradzuje právo kedykoľvek upraviť obsah a funkcie webu.",
      ],
    },
    {
      h: "6. Ochrana osobných údajov",
      p: [
        "Spracovanie osobných údajov sa riadi samostatným dokumentom Ochrana osobných údajov (GDPR), ktorý je dostupný na tejto stránke.",
      ],
    },
    {
      h: "7. Dozor a alternatívne riešenie sporov",
      p: [
        "Orgánom dozoru je Slovenská obchodná inšpekcia (SOI), Inšpektorát SOI pre Bratislavský kraj, Bajkalská 21/A, 820 07 Bratislava (www.soi.sk).",
        "Ak spotrebiteľ nie je spokojný so spôsobom vybavenia reklamácie alebo sa domnieva, že boli porušené jeho práva, má právo obrátiť sa na predávajúceho so žiadosťou o nápravu. Ak predávajúci odpovie zamietavo alebo neodpovie do 30 dní, spotrebiteľ má právo podať návrh na začatie alternatívneho riešenia sporu (ARS) subjektu ARS v zmysle zákona č. 391/2015 Z. z. Spotrebiteľ môže využiť aj európsku platformu RSO (riešenie sporov online) dostupnú na https://ec.europa.eu/consumers/odr.",
      ],
    },
    {
      h: "8. Záverečné ustanovenia",
      p: [
        "Tieto obchodné podmienky sa riadia právnym poriadkom Slovenskej republiky. Prevádzkovateľ si vyhradzuje právo tieto podmienky meniť; aktuálne znenie je vždy zverejnené na tejto stránke.",
      ],
    },
  ],
};

const COOKIES = {
  title: "Používanie cookies",
  slug: "pouzivanie-cookies",
  intro: "Táto stránka vysvetľuje, čo sú súbory cookies, aké typy používame na webe Zateplenia.eu a ako môžete ich používanie ovplyvniť.",
  sections: [
    {
      h: "1. Čo sú cookies",
      p: [
        "Cookies sú malé textové súbory, ktoré sa ukladajú do vášho prehliadača pri návšteve webovej stránky. Umožňujú stránke zapamätať si vaše nastavenia a pomáhajú nám merať a zlepšovať fungovanie webu.",
      ],
    },
    {
      h: "2. Aké cookies používame",
      p: ["Na našom webe využívame nasledujúce kategórie:"],
      li: [
        "Nevyhnutné (technické) — zabezpečujú základné fungovanie stránky a nie je možné ich vypnúť.",
        "Analytické — anonymizovane merajú návštevnosť a správanie na stránke (prezerané sekcie, zdroj návštevy), aby sme mohli zlepšovať obsah. Nepoužívame ich na identifikáciu jednotlivcov.",
        "Preferenčné — zapamätajú si vaše voľby (napr. rozpracované údaje v kalkulačke) pre pohodlnejšie používanie.",
      ],
    },
    {
      h: "3. Ako spravovať cookies",
      p: [
        "Ukladanie cookies môžete kedykoľvek obmedziť alebo zakázať v nastaveniach svojho prehliadača (sekcia Súkromie / Ochrana údajov). Vypnutie niektorých cookies však môže ovplyvniť funkčnosť stránky.",
      ],
    },
    {
      h: "4. Doba uchovávania",
      p: [
        "Krátkodobé (relačné) cookies sa vymažú po zatvorení prehliadača. Dlhodobé cookies zostávajú uložené po dobu potrebnú na daný účel, prípadne dokým ich sami nevymažete.",
      ],
    },
    {
      h: "5. Súvisiace dokumenty",
      p: [
        "Spracovanie osobných údajov je podrobne popísané v dokumente Ochrana osobných údajov (GDPR), ktorý je dostupný na tejto stránke.",
      ],
    },
  ],
};

const COMPLAINTS = {
  title: "Reklamačný poriadok",
  slug: "reklamacny-poriadok",
  intro: "Tento reklamačný poriadok upravuje spôsob a podmienky uplatnenia a vybavenia reklamácií pri službách sprostredkovania a realizácie zateplenia poskytovaných spoločnosťou RESTOB s.r.o.",
  sections: [
    {
      h: "1. Poskytovateľ",
      p: [
        `Poskytovateľom je ${COMPANY.name}, so sídlom ${COMPANY.street}, ${COMPANY.city}, IČO: ${COMPANY.ico}, DIČ: ${COMPANY.dic}, IČ DPH: ${COMPANY.icdph}, zapísaná v Obchodnom registri ${COMPANY.court}, oddiel: ${COMPANY.section}, vložka č. ${COMPANY.insert} (ďalej len „poskytovateľ").`,
      ],
    },
    {
      h: "2. Rozsah zodpovednosti — sprostredkovateľ alebo zhotoviteľ",
      p: ["Poskytovateľ môže voči zákazníkovi vystupovať v dvoch rôznych rolách. Konkrétna rola je vždy uvedená v príslušnej cenovej ponuke alebo zmluve:"],
      li: [
        "Sprostredkovateľ — poskytovateľ sprostredkuje kontakt medzi zákazníkom a overeným realizačným partnerom. Zmluva o dielo sa uzatvára priamo medzi zákazníkom a partnerom a za vady diela (kvalitu realizácie) zodpovedá realizačný partner. Poskytovateľ v tejto role zodpovedá za riadne sprostredkovanie služby.",
        "Zhotoviteľ (realizátor) — poskytovateľ uzatvára zmluvu o dielo priamo so zákazníkom a sám zodpovedá za vady diela vrátane poskytnutej záruky.",
      ],
    },
    {
      h: "3. Záruka",
      p: [
        "Ak poskytovateľ vystupuje ako zhotoviteľ, poskytuje na vykonané práce záruku 36 mesiacov a na dodaný materiál 60 mesiacov, ak nie je v zmluve dohodnuté inak. Ak vystupuje ako sprostredkovateľ, záruku na dielo poskytuje realizačný partner podľa uzatvorenej zmluvy o dielo.",
      ],
    },
    {
      h: "4. Uplatnenie reklamácie",
      p: [
        `Reklamáciu je možné uplatniť písomne e-mailom na ${COMPANY.email} alebo poštou na adresu sídla ${COMPANY.street}, ${COMPANY.city}. Ak za vady diela zodpovedá realizačný partner, poskytovateľ reklamáciu bezodkladne postúpi príslušnému partnerovi a informuje o tom zákazníka.`,
        "V reklamácii uveďte: meno a kontaktné údaje, číslo zmluvy alebo cenovej ponuky, opis vady a spôsob, akým sa prejavuje, prípadne fotodokumentáciu.",
      ],
    },
    {
      h: "5. Lehota na vybavenie",
      p: [
        "Prijatie reklamácie poskytovateľ potvrdí bezodkladne. Reklamácia spotrebiteľa bude vybavená najneskôr do 30 dní odo dňa jej uplatnenia, ak sa so zákazníkom nedohodne dlhšia lehota. O výsledku vybavenia je zákazník informovaný.",
      ],
    },
    {
      h: "6. Spôsoby vybavenia reklamácie",
      li: [
        "bezplatné odstránenie vady (oprava),",
        "primeraná zľava z ceny diela,",
        "výmena vadného materiálu alebo časti diela,",
        "odstúpenie od zmluvy pri neodstrániteľnej vade brániacej riadnemu užívaniu.",
      ],
    },
    {
      h: "7. Odstúpenie od zmluvy",
      p: [
        "Spotrebiteľ, ktorý uzatvoril zmluvu na diaľku alebo mimo prevádzkových priestorov, má právo odstúpiť od zmluvy do 14 dní bez uvedenia dôvodu podľa zákona č. 102/2014 Z. z. Toto právo sa nevzťahuje na zmluvu o poskytnutí služby, ak sa služba začala poskytovať s výslovným súhlasom spotrebiteľa pred uplynutím lehoty, ani na dielo zhotovené podľa osobitných požiadaviek spotrebiteľa (na mieru). Podrobnosti sú uvedené v konkrétnej zmluve.",
      ],
    },
    {
      h: "8. Dozor a alternatívne riešenie sporov",
      p: [
        "Orgánom dozoru je Slovenská obchodná inšpekcia (SOI), Inšpektorát SOI pre Bratislavský kraj, Bajkalská 21/A, 820 07 Bratislava (www.soi.sk). Spotrebiteľ sa môže obrátiť na poskytovateľa so žiadosťou o nápravu; ak poskytovateľ odpovie zamietavo alebo neodpovie do 30 dní, má právo podať návrh na alternatívne riešenie sporu (ARS) podľa zákona č. 391/2015 Z. z., prípadne využiť platformu RSO na https://ec.europa.eu/consumers/odr.",
      ],
    },
  ],
};

const CONTRACT = {
  title: "Zmluva o dielo (vzor)",
  slug: "zmluva-o-dielo",
  intro: "Toto je vzorové znenie zmluvy o dielo na zhotovenie zateplenia. Konkrétne znenie sa dohodne individuálne v písomnej zmluve; údaje v hranatých zátvorkách sa doplnia podľa konkrétnej zákazky. Podľa dohody vystupuje ako zhotoviteľ buď RESTOB s.r.o., alebo ním určený overený realizačný partner (sprostredkovanie).",
  sections: [
    {
      h: "Čl. I — Zmluvné strany",
      li: [
        "Objednávateľ: [meno a priezvisko / obchodné meno], [adresa / sídlo], [dátum narodenia / IČO], (ďalej len „objednávateľ“).",
        `Zhotoviteľ: ${COMPANY.name}, ${COMPANY.street}, ${COMPANY.city}, IČO: ${COMPANY.ico}, DIČ: ${COMPANY.dic}, IČ DPH: ${COMPANY.icdph}, zapísaná v OR ${COMPANY.court}, oddiel: ${COMPANY.section}, vložka č. ${COMPANY.insert}, alebo ním určený overený realizačný partner (ďalej len „zhotoviteľ“).`,
      ],
    },
    {
      h: "Čl. II — Predmet a rozsah diela",
      p: ["Predmetom zmluvy je zhotovenie diela — zateplenie fasády objektu na adrese [adresa objektu] v rozsahu: plocha [X] m², zatepľovací systém ETICS [materiál a hrúbka], povrchová úprava [typ omietky], vrátane [lešenie / doprava / klampiarske práce podľa cenovej ponuky]."],
    },
    {
      h: "Čl. III — Cena diela a platobné podmienky",
      p: ["Cena diela je stanovená podľa cenovej ponuky č. [číslo] vo výške [suma] € vrátane DPH. Platobné podmienky: [záloha % / splátky / doplatok po odovzdaní]. Cena zahŕňa materiál, práce a služby uvedené v ponuke."],
    },
    {
      h: "Čl. IV — Termín realizácie",
      p: ["Zhotoviteľ vykoná dielo v termíne od [dátum] do [dátum]. Termín sa môže primerane predĺžiť z dôvodu nepriaznivého počasia alebo okolností vylučujúcich zodpovednosť (vyššia moc)."],
    },
    {
      h: "Čl. V — Práva a povinnosti strán",
      li: [
        "Objednávateľ zabezpečí zhotoviteľovi prístup k objektu a odber elektriny a vody v potrebnom rozsahu.",
        "Zhotoviteľ vykoná dielo v dohodnutej kvalite, v súlade s technologickými postupmi výrobcu systému a platnými normami.",
        "Objednávateľ poskytne súčinnosť pri odovzdaní a prevzatí diela.",
      ],
    },
    {
      h: "Čl. VI — Záruka za dielo",
      p: ["Zhotoviteľ poskytuje záruku na vykonané práce v dĺžke 36 mesiacov a na dodaný materiál 60 mesiacov odo dňa odovzdania diela, ak nie je dohodnuté inak."],
    },
    {
      h: "Čl. VII — Odovzdanie a prevzatie diela",
      p: ["O odovzdaní a prevzatí diela sa spíše preberací protokol, v ktorom sa uvedú prípadné vady a nedorobky a termín ich odstránenia."],
    },
    {
      h: "Čl. VIII — Reklamácie",
      p: ["Práva zo zodpovednosti za vady a spôsob ich uplatnenia sa riadia reklamačným poriadkom zhotoviteľa a príslušnými ustanoveniami Občianskeho, resp. Obchodného zákonníka."],
    },
    {
      h: "Čl. IX — Odstúpenie od zmluvy",
      p: ["Ktorákoľvek zo strán môže od zmluvy odstúpiť z dôvodov uvedených v zmluve alebo vyplývajúcich zo zákona. Spotrebiteľ je poučený o práve odstúpiť od zmluvy uzatvorenej na diaľku alebo mimo prevádzkových priestorov v lehote 14 dní podľa zákona č. 102/2014 Z. z."],
    },
    {
      h: "Čl. X — Záverečné ustanovenia",
      p: ["Zmluva sa vyhotovuje v dvoch rovnopisoch, po jednom pre každú stranu. Vzťahy neupravené touto zmluvou sa riadia právnym poriadkom Slovenskej republiky. Zmluva nadobúda platnosť a účinnosť dňom podpisu oboch strán."],
    },
    {
      h: "Podpisy",
      p: ["V [miesto], dňa [dátum].", "Objednávateľ: ________________________     Zhotoviteľ: ________________________"],
    },
  ],
};

const WITHDRAWAL = {
  title: "Odstúpenie od zmluvy (vzorový formulár)",
  slug: "odstupenie-od-zmluvy",
  intro: "Vzorový formulár na odstúpenie od zmluvy uzatvorenej na diaľku alebo mimo prevádzkových priestorov podľa zákona č. 102/2014 Z. z. Vyplňte a doručte nám ho iba v prípade, že si želáte odstúpiť od zmluvy v 14-dňovej lehote.",
  sections: [
    {
      h: "Komu sa odstúpenie zasiela",
      p: [`${COMPANY.name}, ${COMPANY.street}, ${COMPANY.city}, e-mail: ${COMPANY.email}`],
    },
    {
      h: "Vyhlásenie",
      p: ["Týmto oznamujem, že odstupujem od zmluvy na túto službu / dielo: ................................................................................"],
    },
    {
      h: "Údaje",
      li: [
        "Dátum objednania / uzatvorenia zmluvy: ..............................",
        "Číslo zmluvy alebo cenovej ponuky: ..............................",
        "Meno a priezvisko spotrebiteľa: ..............................",
        "Adresa spotrebiteľa: ..............................",
        "Dátum: ..............................",
        "Podpis spotrebiteľa (ak sa formulár podáva v listinnej podobe): ..............................",
      ],
    },
    {
      h: "Poučenie",
      p: [
        "Právo odstúpiť od zmluvy do 14 dní bez uvedenia dôvodu sa nevzťahuje na službu, ktorá už bola úplne poskytnutá s výslovným súhlasom spotrebiteľa, ani na dielo zhotovené podľa osobitných požiadaviek spotrebiteľa (na mieru). Podrobnosti nájdete v Reklamačnom poriadku a Obchodných podmienkach.",
      ],
    },
  ],
};

const DOCS = { gdpr: GDPR, terms: TERMS, cookies: COOKIES, complaints: COMPLAINTS, contract: CONTRACT, withdrawal: WITHDRAWAL };

export default function Legal({ type }) {
  const doc = DOCS[type] || GDPR;
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadHtmlPdf(legalDocHtml(doc, COMPANY), `${doc.slug}.pdf`);
      toast.success("PDF stiahnuté.");
    } catch {
      toast.error("Nepodarilo sa vytvoriť PDF.");
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    document.title = `${doc.title} | Zateplenia.eu`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", doc.intro);
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    c.setAttribute("href", `${SITE_URL}/${doc.slug}`);
  }, [doc]);

  return (
    <div className="bg-ink min-h-screen" data-testid={`legal-${type}`}>
      <Header />
      <section className="pt-32 pb-24">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <nav className="flex items-center gap-2 text-white/40 text-xs font-mono2 uppercase tracking-widest mb-8">
            <Link to="/" className="hover:text-gold">Domov</Link><span>/</span>
            <span className="text-gold">{doc.title}</span>
          </nav>
          <h1 className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-6">{doc.title}</h1>
          <p className="text-white/60 leading-relaxed text-base md:text-lg mb-6 max-w-2xl">{doc.intro}</p>

          <button
            onClick={downloadPdf}
            disabled={pdfBusy}
            data-testid="legal-download-pdf"
            className="inline-flex items-center gap-2 border border-gold/50 text-gold px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-50 mb-12"
          >
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Stiahnuť ako PDF
          </button>

          <div className="space-y-10">
            {doc.sections.map((s) => (
              <div key={s.h}>
                <h2 className="font-display text-2xl md:text-3xl text-white mb-4">{s.h}</h2>
                {(s.p || []).map((para, i) => (
                  <p key={i} className="text-white/60 leading-relaxed mb-3">{para}</p>
                ))}
                {s.li && (
                  <ul className="list-disc pl-5 space-y-2 text-white/60 leading-relaxed">
                    {s.li.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="mt-14 pt-8 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-3 text-sm">
            <Link to="/ochrana-osobnych-udajov" className="text-white/60 hover:text-gold transition-colors">Ochrana osobných údajov</Link>
            <Link to="/obchodne-podmienky" className="text-white/60 hover:text-gold transition-colors">Obchodné podmienky</Link>
            <Link to="/reklamacny-poriadok" className="text-white/60 hover:text-gold transition-colors">Reklamačný poriadok</Link>
            <Link to="/zmluva-o-dielo" className="text-white/60 hover:text-gold transition-colors">Zmluva o dielo</Link>
            <Link to="/odstupenie-od-zmluvy" className="text-white/60 hover:text-gold transition-colors">Odstúpenie od zmluvy</Link>
            <Link to="/pouzivanie-cookies" className="text-white/60 hover:text-gold transition-colors">Používanie cookies</Link>
            <Link to="/" className="text-gold hover:text-gold-light transition-colors">Späť na hlavnú stránku →</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
